import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import * as workerTimers from 'worker-timers';
import { useDispatch } from 'react-redux';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import { DigitalSubmissionMeta } from '../../redux/types';
import ApiClient from '../../loopback/apiClient';
import { updateDigitalSubmissionMeta } from '../../redux/actions';
import GlobalProcedures from '../../core/globalProcedures';

type Props = {
  trialId: number;
  surveyId: number;
  sectionId: number;
  finalized?: boolean;
}

/**
 * @param startTime UNIX epoch start time in milliseconds
 * @param endTime UNIX epoch end time in milliseconds
 * @returns - array with remaining (Hours, Minutes and seconds)
 */
export function getRemainingTimeCounter(startTime: number, endTime: number): number[] {
  let parts = [0, 0, 0];
  const timeDiff = endTime - startTime;
  if (timeDiff > 0) {
    const dateObj = new Date(timeDiff);
    parts = [
      // using getUTCHours because getHours will return the local hours
      dateObj.getUTCHours(),
      dateObj.getUTCMinutes(),
      dateObj.getUTCSeconds(),
    ];
  }
  return parts;
}

const DigitalTimer: React.FC<Props> = ({
  trialId,
  surveyId,
  sectionId,
  finalized = false,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const timeToEnd = useRef<number>(); // UNIX epoch format in milliseconds
  const nextCheckin = useRef<number>(); // unix epoch format in milliseconds
  const interval = useRef<NodeJS.Timeout>();
  const loggedOut = useRef<boolean>(false);
  const [timer, setTimer] = useState<string>('00:00:00');

  const pause = useCallback(() => {
    navigate(`/digitaltrial/${trialId}/survey/${surveyId}/antibodies`);
  }, [navigate, surveyId, trialId]);

  const checkinTime = useCallback(async (): Promise<DigitalSubmissionMeta> => {
    const dsm: DigitalSubmissionMeta = await ApiClient.checkInTime(trialId, sectionId);
    dispatch(updateDigitalSubmissionMeta(dsm));
    return dsm;
  }, [dispatch, sectionId, trialId]);

  const checkinPause = useCallback(async (): Promise<void> => {
    try {
      const dsm: DigitalSubmissionMeta = await ApiClient.checkInTime(trialId, sectionId, true);
      dispatch(updateDigitalSubmissionMeta(dsm));
    // eslint-disable-next-line no-empty
    } catch (e) { }
  }, [dispatch, sectionId, trialId]);

  useEffect(() => {
    const onInterval = () => {
      if (timeToEnd.current) {
        const timeDiff = timeToEnd.current - Date.now();
        if (timeDiff <= 0 && interval.current) {
          // exit - time limit reached
          clearInterval(interval.current);
          checkinTime().then(() => {
            setTimer('00:00:00');
          });
        } else {
          const parts = getRemainingTimeCounter(Date.now(), timeToEnd.current);
          setTimer(parts.map((v) => String(v).padStart(2, '0')).join(':'));
        }
      }
    };
    interval.current = setInterval(onInterval, 1000);
    return (): void => {
      /** Pause the timer whenever this DigitalTimer component is unmounted */
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [dispatch, checkinTime]);

  useEffect(() => {
    if (finalized) {
      if (interval.current) {
        clearInterval(interval.current);
      }
    }
  }, [finalized]);

  useEffect(() => {
    let timeoutId: number|null;
    /** syncs the timestamps from the server to the frontend */
    const checkinAndSetTimes = async (): Promise<DigitalSubmissionMeta|undefined> => {
      let dsm: DigitalSubmissionMeta;
      try {
        dsm = await checkinTime();
      } catch (e) {
        pause();
        return undefined;
      }
      if (dsm.nextCheckpoint && dsm.endTime) {
        timeToEnd.current = new Date(dsm.endTime).getTime();
        const nextTimeIn = new Date(dsm.nextCheckpoint);
        nextTimeIn.setUTCSeconds(nextTimeIn.getUTCSeconds() - 6);
        nextCheckin.current = nextTimeIn.getTime();
        const timeDiff = timeToEnd.current - Date.now();
        if (!dsm.submissionFinalized && timeDiff > 0) {
          if (timeoutId) {
            workerTimers.clearTimeout(timeoutId);
          }
          timeoutId = workerTimers.setTimeout(() => {
            checkinAndSetTimes();
            // console.log(Date.now() - nextTimeIn.getTime());
          }, nextTimeIn.getTime() - Date.now());
        }
      }
      return dsm;
    };

    const unmountComponent = async () => {
      if (timeoutId !== null && timeoutId !== undefined) {
        workerTimers.clearTimeout(timeoutId);
        timeoutId = null;
      }
      await checkinPause();
    };

    const logoutHandler = async (): Promise<void> => new Promise((resolve) => {
      loggedOut.current = true;
      unmountComponent().then(resolve);
    });

    checkinAndSetTimes();
    // register global procedure that will be triggered before logout
    const globalProcedures = GlobalProcedures.getInstance();
    globalProcedures.register('logout', logoutHandler);

    return (): void => {
      globalProcedures.deregister('logout');
      if (!loggedOut.current) {
        unmountComponent();
      }
    };
  }, [checkinTime, checkinPause, dispatch, pause]);

  return (
    <>
      {
        !finalized && (
          <>
            <Box
              sx={{
                color: 'secondary.dark',
                p: 1,
                ml: 1,
                mr: 1,
              }}
            >
              <Typography variant="h6">{timer}</Typography>
            </Box>
            <Tooltip title="Pause the Exam">
              <IconButton color="secondary" onClick={pause} disabled={finalized}><PauseIcon /></IconButton>
            </Tooltip>
          </>
        )
      }
    </>
  );
};

export default DigitalTimer;
